import { StringHelpers } from '@dai/common'
import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import { groupBy } from 'lodash'
import { useMemo } from 'react'

import { PermissionFragment } from '@/graphql'
import useT from '@/i18n/useT'

type Props = {
  permissions: Array<PermissionFragment>
  selectedPermissionIds: Array<string>
  onTogglePermission: (permissionId: string) => void
}

export const PermissionsSelectionList: React.FC<Props> = props => {
  const { permissions, selectedPermissionIds, onTogglePermission } = props
  const { t } = useT()

  const permissionsWithLabels = useMemo(
    () =>
      permissions.map(permission => {
        const name = permission.codename.toLowerCase()
        const defaultLabel = StringHelpers.snakeCaseToTitleCase(name)
        const labelKey = `permissions.title.${name}`
        const descriptionKey = `permissions.description.${name}`
        const categoryKey = `permissions.category.${permission.category.toLowerCase()}`
        return {
          ...permission,
          // @tolgee-ignore
          label: t(labelKey, defaultLabel),
          // @tolgee-ignore
          description: t(descriptionKey, ''),
          // @tolgee-ignore
          category: t(
            categoryKey,
            StringHelpers.snakeCaseToTitleCase(
              permission.category,
            ).toUpperCase(),
          ),
        }
      }),
    [permissions, t],
  )

  const permissionsByCategory = groupBy(
    permissionsWithLabels,
    permission => permission.category,
  )

  return (
    <>
      {Object.entries(permissionsByCategory).map(([category, permissions]) => (
        <Box key={category}>
          <Typography variant="subtitle2" gutterBottom>
            {category}
          </Typography>
          <Stack>
            {permissions.map(permission => (
              <Box key={permission.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPermissionIds.includes(permission.id)}
                      onChange={() => onTogglePermission(permission.id)}
                    />
                  }
                  label={permission.label}
                />
                {permission.description &&
                  !permission.description.includes(
                    permission.codename.toLowerCase(),
                  ) && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ ml: 4 }}
                    >
                      {permission.description}
                    </Typography>
                  )}
              </Box>
            ))}
          </Stack>
        </Box>
      ))}
    </>
  )
}
